import { Component, HostListener, OnInit, inject } from '@angular/core';
import { AuthApimService, AuthOidcService } from '@cddng/auth-oidc-apim';
import { environment } from 'src/environments/environment';
import { AppContext } from 'src/app/shared/services/app.context';
import { Router, RouterOutlet } from '@angular/router';
import { AuthUserDto } from 'src/app/shared/dto/authUserDto';
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { I2A } from "./shared/enums/i2a.enum";
import { HttpClient } from "@angular/common/http";
import { I2aComplementarityDatas } from "./shared/models/authentification/i2a-complementarity-data";
import { concatMap, Observable, of, switchMap } from "rxjs";
import { apiurl} from "./shared/config/apiurl.config";
import { catchError, filter } from "rxjs/operators";
import { URL_PAGE } from "./shared/enums/url.pages.enum";
import { Role } from "src/app/shared/enums/role.enum";
import { BandeauEnteteComponent } from './home/bandeau-entete/bandeau-entete.component';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [BandeauEnteteComponent, RouterOutlet],
  styleUrls: [ './app.component.scss' ],
  standalone: true
})
export class AppComponent implements OnInit {
  authOidcService = inject(AuthOidcService);
  context = inject(AppContext);
  private readonly router = inject(Router);
  authApimService = inject(AuthApimService);
  http = inject(HttpClient);

  title = 'Mise en expédition';
  applicationDescription = 'Branche SERVICES - COURRIER - COLIS';
  path: string = '/' + URL_PAGE.HOME;

  constructor() {
    // Initialisation et appel du process d'autologin
    this.authOidcService.autologin('/autologin').pipe(untilDestroyed(this)).subscribe();
  }

  ngOnInit(): void {
    this.path = window.location.pathname !== '/' ? window.location.pathname : '/home';

    if (environment.byPassI2a) {
      this.manageAuthenticationWithBypassI2a();
    } else {
      this.manageAuthenticationWithoutBypassI2a();
    }
    this.context.setBandeauScript();
  }

  /**
   * manage authentication without I2a authentication
   */
  manageAuthenticationWithBypassI2a(): void {
    const DOMMY_USER = new AuthUserDto();
    DOMMY_USER.roles.push( Role.ADMIN );
    DOMMY_USER.idRh = 'BYPASS';
    DOMMY_USER.regateCode = '932440';
    DOMMY_USER.isAdmin = true;

    this.context.authenticatedUser = DOMMY_USER;
    this.redirectAfterIdentification();
  }

  /**
   * manage authentication with I2a authentication
   */
  manageAuthenticationWithoutBypassI2a(): void {
    // Abonnement à l'observable de changement d'état connecté/déconnecté
    this.authOidcService?.sessionChanged$?.pipe(
      switchMap(auth => {
        console.log("[OIDC AUTH] ", auth);
        if (auth) {
          this.context.authenticatedUser = this.authOidcService.getAuthenticatedUserFromJwt();
          return this.authApimService.sessionChanged$;
        } else {
          this.context.authenticatedUser = null;
          return of(false);
        }
      }),
      filter((value: boolean) => value),
      switchMap(hasToken => {
        console.log("[APIM TOKEN] ", hasToken);
        if (this.context.authenticatedUser?.type === I2A.EXTERNAL_USER) {
          return this.setRegateCodeForExternalUser();
        } else {
          this.redirectAfterIdentification();
          console.log('AUTH USER', this.context.authenticatedUser);
          return of(true);
        }
      })
    ).subscribe({
      error: () => {
        console.log("Erreur AUTH USER")
        this.router.navigate([URL_PAGE.UNAUTHORIZED]);
      }
    });
  }

  /** Corporate header: disconnect button listener */
  @HostListener('window:UserDisconnectBtnClicked') userLogout(): void {
    this.authOidcService.logout();
  }

  /**
   * redirect to unauthorized page if user do not have right
   */
  redirectAfterIdentification(): void {
    if (!this.currentUserHasRole()) {
      this.router.navigate([URL_PAGE.UNAUTHORIZED]);
    } else if (this.path && this.currentUserHasRole() && this.isHomePage()) {
      this.router.navigate([this.path]);
    }
  }

  isHomePage(): boolean {
    return this.router.url.includes(URL_PAGE.HOME) || this.router.url.includes(URL_PAGE.BIENVENUE);
  }

  /**
   * dertermine if current user has role to use the application else redirect to unauthorized page
   */
  currentUserHasRole(): boolean {
    return this.context.currentUserHasRole();
  }

  /**
   * Get complementarity data from user in I2A
   * @param idUser
   * @param role
   */
  getComplementarityData(idUser: string, role: string): Observable<I2aComplementarityDatas> {
    return this.http.get<I2aComplementarityDatas>(`${apiurl.i2a.complementarityDataForExternalUser}`, {
      params: {
        idUser: idUser,
        role: role
      }
    });
  }

  /**
   * Get complementarity data for SPID users to set regate code
   */
  setRegateCodeForExternalUser() {
    const role = this.context.authenticatedUser?.roles.find(obj => obj.includes(I2A.ROLE_SPID_OPEX));
    return this.getComplementarityData(this.context.authenticatedUser?.idRh, role).pipe(
      concatMap(data => {
        const value = data.dcus.find(complementarityData => complementarityData.attribut === I2A.REGATE);
        this.context.authenticatedUser.regateCode = value ? value.valeur : null;
        this.redirectAfterIdentification();
        console.log('AUTH USER', this.context.authenticatedUser);
        return of(true);
      }),
      catchError(() => {
        this.router.navigate([URL_PAGE.UNAUTHORIZED]);
        throw new Error();
      })
    );
  }
}
